// frontend/src/components/dashboard/RecentActivity.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function RecentActivity({ activities = [], limit = 10, showViewAll = false }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // 'all', 'invoice', 'payment'

  // Filtrage local des données reçues du Dashboard
  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    return activity.type === filter;
  });

  // Limitation du nombre d'éléments
  const displayActivities = filteredActivities.slice(0, limit);

  // Fonction pour formater la date proprement
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  // Icônes et couleurs selon le type
  const getTypeStyles = (type, status) => {
    if (type === 'invoice') {
      return {
        icon: (
          <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        bg: 'bg-blue-100',
        text: status === 'paid' ? 'Payée' : status === 'sent' ? 'Envoyée' : 'Brouillon'
      };
    }
    return {
      icon: (
        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bg: 'bg-green-100',
      text: 'Paiement reçu'
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'TND' }).format(amount);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 h-full flex flex-col">
      {/* En-tête avec Filtres */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-gray-800">Activités récentes</h3>
        <div className="flex gap-2 text-xs">
          <button 
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-full transition-colors ${filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Tout
          </button>
          <button 
            onClick={() => setFilter('invoice')}
            className={`px-3 py-1.5 rounded-full transition-colors ${filter === 'invoice' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Factures
          </button>
          <button 
            onClick={() => setFilter('payment')}
            className={`px-3 py-1.5 rounded-full transition-colors ${filter === 'payment' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Paiements
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-y-auto pr-2 space-y-4 max-h-[400px]">
        {displayActivities.length > 0 ? (
          displayActivities.map((item, index) => {
            const style = getTypeStyles(item.type, item.status);
            return (
              <div key={`${item.type}-${item.id}-${index}`} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0`}>
                  {style.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.title}
                    </p>
                    <span className="text-xs font-semibold text-gray-900 whitespace-nowrap ml-2">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {item.clientName} • {style.text}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {formatDate(item.date)}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-10 text-gray-400 text-sm">
            Aucune activité récente.
            <br/>
            <span className="text-xs">Créez une facture ou un paiement pour voir apparaître des données.</span>
          </div>
        )}
      </div>

      {/* Pied de carte */}
      {showViewAll && (
        <div className="mt-4 pt-4 border-t border-gray-100 text-center">
          <button 
            onClick={() => navigate(filter === 'payment' ? '/payments' : '/invoices')}
            className="text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors"
          >
            Voir tout l'historique &rarr;
          </button>
        </div>
      )}
    </div>
  );
}